import produce from "immer";
import { createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { Comment, Violation } from "../../../Types/UnitFeatureTypes";


export interface FetchViolationInfoState {
  request: {
      loading: boolean;
      error: string | null;
      singleViolation: Violation | null;
      comments: Comment[] | null;
      data: Violation[] | null;
      count: string | null;
  };
}

export enum Types {
  FETCH_VIOLATION_REQUEST = "FETCH_VIOLATION_REQUEST",
  FETCH_VIOLATION_SUCCESS = "FETCH_VIOLATION_SUCCESS",
  FETCH_VIOLATION_FAILURE = "FETCH_VIOLATION_FAILURE",
  FETCH_VIOLATION_COMMENT_REQUEST = "FETCH_VIOLATION_COMMENT_REQUEST",
  FETCH_VIOLATION_COMMENT_SUCCESS = "FETCH_VIOLATION_COMMENT_SUCCESS",
  FETCH_VIOLATION_COMMENT_FAILURE = "FETCH_VIOLATION_COMMENT_FAILURE",
}

const violationActions = createAsyncAction(
  Types.FETCH_VIOLATION_REQUEST,
  Types.FETCH_VIOLATION_SUCCESS,
  Types.FETCH_VIOLATION_FAILURE,
)
<
  { id?: string, offset?: number, sortColumn?: string, sortDirection?: string },
  { violations?: Violation[], violation?: Violation, count: string },
  { message: string }
>();

const violationCommentActions = createAsyncAction(
  Types.FETCH_VIOLATION_COMMENT_REQUEST,
  Types.FETCH_VIOLATION_COMMENT_SUCCESS,
  Types.FETCH_VIOLATION_COMMENT_FAILURE,
)
<
  { id: number },
  { comments: Comment[] },
  { message: string }
>();

export const Actions = { violationActions, violationCommentActions };

export const initialState: FetchViolationInfoState = {
  request: {
    loading: false,
    error: null as string | null,
    singleViolation: null,
    comments: null,
    data: null,
    count: '0'
  }
};

export type FetchViolationState = typeof initialState;

const fetchViolationRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.comments = null;
    draft.request.error = null;
    draft.request.data = null;

    return draft;
  });

const fetchViolationSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.violationActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.data = payload.violations ?? null;
    draft.request.singleViolation = payload.violation ?? null;
    draft.request.count = payload.count;
    if (draft.request.data) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchViolationFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.violationActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const fetchViolationCommentRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.comments = null;

    return draft;
  });

const fetchViolationCommentSuccess = (
  state = initialState,
  { payload }: ActionType<typeof violationCommentActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.comments = payload.comments;
    if (draft.request.comments) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchViolationCommentFailure = (
  state = initialState,
  { payload }: ActionType<typeof violationCommentActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.violationActions.request, fetchViolationRequest)
  .handleAction(Actions.violationActions.success, fetchViolationSuccess)
  .handleAction(Actions.violationActions.failure, fetchViolationFailure)
  .handleAction(Actions.violationCommentActions.request, fetchViolationCommentRequest)
  .handleAction(Actions.violationCommentActions.success, fetchViolationCommentSuccess)
  .handleAction(Actions.violationCommentActions.failure, fetchViolationCommentFailure);
  